import {
	Button,
	DateInputComponent,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { ReceiptPaymentSchema, ReceiptPaymentTypeEnum } from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { parseDate } from "@internationalized/date";
import { PenIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { useUpdateReceiptPaymentMutation } from "../../../api/queries/receipt.queries";
import { ControlledPriceInput } from "../../../components/inputs/ControlledPriceInput";

const normalizeDecimalText = (value: string): string => {
	const normalized = value.replace(",", ".");
	return normalized;
};

const updatePaymentFormSchema = z.object({
	paymentDate: z
		.custom<string>()
		.refine((val) => val !== undefined && val !== "", {
			message: "La date de règlement est requise",
		}),
	amount: z
		.string()
		.min(1, "Le montant est requis")
		.refine(
			(value) => {
				const normalized = normalizeDecimalText(value);
				return !Number.isNaN(Number(normalized)) && Number(normalized) > 0;
			},
			{ message: "Le montant doit être un nombre positif" },
		),
	type: z.enum(ReceiptPaymentTypeEnum, {
		error: "Le type de paiement est requis",
	}),
	paymentReference: z.string().min(1, "La référence de paiement est requise"),
});

type UpdatePaymentFormSchema = z.infer<typeof updatePaymentFormSchema>;

interface UpdatePaymentReceiptModalProps {
	payment: ReceiptPaymentSchema;
	receiptId: number;
	onSuccess?: () => void;
}

const UpdatePaymentReceiptModal = ({
	payment,
	receiptId,
	onSuccess,
}: UpdatePaymentReceiptModalProps) => {
	const [open, setOpen] = useState(false);
	const updatePaymentMutation = useUpdateReceiptPaymentMutation();

	const form = useForm<UpdatePaymentFormSchema>({
		resolver: zodResolver(updatePaymentFormSchema),
		defaultValues: {
			paymentDate: payment.paymentDate.toString().split("T")[0],
			amount: payment.amount.toString(),
			type: payment.type,
			paymentReference: payment.paymentReference,
		},
		mode: "onChange",
	});

	const handleOpenChange = useCallback(
		(newOpen: boolean) => {
			setOpen(newOpen);
			if (!newOpen) {
				form.reset({
					paymentDate: payment.paymentDate.toString().split("T")[0],
					amount: payment.amount.toString(),
					type: payment.type,
					paymentReference: payment.paymentReference,
				});
			}
		},
		[form, payment],
	);

	const onSubmit = useCallback(
		async (data: UpdatePaymentFormSchema) => {
			try {
				await updatePaymentMutation.mutateAsync({
					id: payment.id,
					receiptId,
					paymentDate: data.paymentDate,
					amount: Number(normalizeDecimalText(data.amount)),
					type: data.type,
					paymentReference: data.paymentReference,
				});

				toast.success("Le règlement a été modifié avec succès");

				handleOpenChange(false);
				onSuccess?.();
			} catch (error) {
				console.error("Erreur lors de la modification du règlement:", error);
				toast.error(
					"Une erreur est survenue lors de la modification du règlement",
				);
			}
		},
		[updatePaymentMutation, payment.id, receiptId, handleOpenChange, onSuccess],
	);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<Button
				size="icon"
				variant="ghost"
				onClick={(e) => {
					e.stopPropagation();
					setOpen(true);
				}}
			>
				<PenIcon className="h-4 w-4" />
			</Button>
			<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Modifier un règlement</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="paymentDate"
								render={({ field }) => (
									<FormItem className="col-span-2">
										<FormLabel>Date de règlement</FormLabel>
										<FormControl>
											<DateInputComponent
												value={field.value ? parseDate(field.value) : undefined}
												onChange={(value) => {
													field.onChange(value?.toString() || "");
												}}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<ControlledPriceInput
								control={form.control}
								name="amount"
								label="Montant"
							/>

							<FormField
								control={form.control}
								name="type"
								render={({ field }) => (
									<FormItem className="col-span-2">
										<FormLabel>Type de paiement</FormLabel>
										<FormControl>
											<Select
												value={field.value}
												onValueChange={field.onChange}
											>
												<SelectTrigger>
													<SelectValue placeholder="Sélectionner un type" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={ReceiptPaymentTypeEnum.CHECK}>
														Chèque
													</SelectItem>
													<SelectItem value={ReceiptPaymentTypeEnum.TRANSFER}>
														Virement
													</SelectItem>
												</SelectContent>
											</Select>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="paymentReference"
								render={({ field }) => (
									<FormItem className="col-span-2">
										<FormLabel>Référence du paiement</FormLabel>
										<FormControl>
											<Input
												{...field}
												type="text"
												placeholder="Référence"
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<DialogFooter className="mt-8 w-full sm:!justify-between">
							<Button
								type="button"
								variant="outline"
								className="flex-1"
								onClick={() => handleOpenChange(false)}
							>
								Annuler
							</Button>
							<Button
								type="submit"
								className="flex-1"
								disabled={updatePaymentMutation.isPending}
								isLoading={updatePaymentMutation.isPending}
							>
								{updatePaymentMutation.isPending ? "Création..." : "Valider"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default UpdatePaymentReceiptModal;
